<script setup lang="ts">
import { ref } from 'vue'
import type { CashBookSection } from '@/api/models/reports/CashbookV2'
import type { CashbookRequest } from '@/api/models/reports/CashbookRequest'
import ApiService from '@/api/ApiService'
import ConversionUtility from '@/utils/ConversionUtility'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useToast } from 'primevue/usetoast'
import CustomButton from '@/components/CustomComponent/CustomButton.vue'

const isLoading = ref(false)
const selectedDate = ref<Date>(new Date())
const cashbook = ref<CashBookSection[]>([])
const isDataExist = ref(false)
const toast = useToast()

function getSectionTotals(transactions: any[] | undefined) {
  if (!transactions) return { receiptCash: 0, paymentCash: 0 }

  return transactions.reduce(
    (totals, tx) => ({
      receiptCash: totals.receiptCash + (tx.receipt?.cash || 0),
      paymentCash: totals.paymentCash + (tx.payments?.cash || 0)
    }),
    { receiptCash: 0, paymentCash: 0 }
  )
}

function getAllSectionTotals() {
  return cashbook.value.reduce(
    (totals, section) => {
      const sectionTotal = getSectionTotals(section.transaction)
      return {
        receiptCash: totals.receiptCash + sectionTotal.receiptCash,
        paymentCash: totals.paymentCash + sectionTotal.paymentCash
      }
    },
    { receiptCash: 0, paymentCash: 0 }
  )
}

function handlePrint() {
  const doc = new jsPDF()
  let y = 15

  doc.setFontSize(20)
  doc.text('Bhavasar Pattin Shakari Sanga(R)', 105, y, { align: 'center' })
  doc.setFontSize(12)
  doc.text('Branch: Badami | Tq: Badami | Dist: Bagalkote', 105, y + 10, { align: 'center' })
  y += 25

  doc.setFontSize(16)
  doc.text('Abstract Cashbook', 14, y)
  doc.setFontSize(12)
  doc.text(`Date: ${selectedDate.value.toLocaleDateString()}`, 150, y)
  y += 10

  const tableBody = cashbook.value.map(section => {
    const totals = getSectionTotals(section.transaction)
    return [
      section.sectionTitle,
      ConversionUtility.toRuppesCoversion(totals.receiptCash),
      ConversionUtility.toRuppesCoversion(totals.paymentCash)
    ]
  })

  const grandTotals = getAllSectionTotals()
  tableBody.push([
    'Grand Total',
    ConversionUtility.toRuppesCoversion(grandTotals.receiptCash),
    ConversionUtility.toRuppesCoversion(grandTotals.paymentCash)
  ])

  autoTable(doc, {
    startY: y + 5,
    head: [['Section', 'Total Receipts', 'Total Payments']],
    body: tableBody,
    styles: { fontSize: 11, cellPadding: 3 },
    theme: 'grid',
    headStyles: { fillColor: [60, 141, 188] },
    footStyles: { fillColor: [230, 230, 230] }
  })

  doc.save(`Abstract_Cashbook_${selectedDate.value.toLocaleDateString()}.pdf`)
}

function getCashbook() {
  if (!selectedDate.value) {
    showToast('Error', 'Please select a date.', 'error')
    return
  }

  isLoading.value = true
  const req: CashbookRequest = { date: selectedDate.value.getTime() }

  ApiService.v2cashbook(req)
    .then((response) => {
      if (!response || !response.data) {
        showToast('Error', 'Invalid response from the server.', 'error')
        return
      }
      if (response.statusCode == 200) {
        isDataExist.value = true
        cashbook.value = response.data.section
      }
    })
    .catch((error) => {
      console.error('API Error:', error)
      showToast('Cashbook', error.message || 'An error occurred', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function showToast(
  summary: string,
  detail: string,
  severity: 'success' | 'info' | 'warn' | 'error'
) {
  toast.add({ severity, summary, detail, life: 5000 })
}
</script>

<template>
  <Toast />
  <main class="main">
    <Card class="h-full overflow-y-scroll pb-20">
      <template #title>
        <h2 class="p-4 text-3xl font-bold">Abstract Cashbook</h2>
      </template>

      <template #content>
        <div class="flex p-4 items-center space-x-4">
          <label for="date" class="text-medium font-medium text-gray-700">Day Book :</label>
          <DatePicker
            :maxDate="new Date()"
            v-model="selectedDate"
            dateFormat="dd/mm/yy"
            id="date"
            showIcon
          />
          <CustomButton title="Fetch Cashbook" @click="getCashbook" />
          <Dialog :visible="isLoading" modal :draggable="false" :closable="false">
            <span>Please wait while we fetch your cashbook.</span>
            <ProgressBar mode="indeterminate" style="height: 6px; margin-top: 1.5rem" />
          </Dialog>
        </div>

        <h2 v-if="isDataExist" class="text-1xl font-bold text-center">
          Bhavasar Pattin Shakari Sanga(R), Badami
        </h2>
        <h5 v-if="isDataExist" class="text-center text-sm">
          Branch: Badami | Tq: Badami | Dist: Bagalkote
        </h5>
        <Divider class="mt-4" />

        <DataTable v-if="isDataExist" :value="cashbook" striped-rows>
          <Column field="sectionTitle" header="Section" />
          <Column header="Total Receipts">
            <template #body="{ data }">
              {{
                ConversionUtility.toStringRupeesConversion(
                  getSectionTotals(data.transaction).receiptCash
                )
              }}
            </template>
          </Column>
          <Column header="Total Payments">
            <template #body="{ data }">
              {{
                ConversionUtility.toStringRupeesConversion(
                  getSectionTotals(data.transaction).paymentCash
                )
              }}
            </template>
          </Column>
        </DataTable>

        <Divider class="my-4" />
        <div v-if="isDataExist" class="text-right font-bold pr-6 text-lg">
          Total — <span>Receipt:
            {{ ConversionUtility.toStringRupeesConversion(getAllSectionTotals().receiptCash) }} |
          </span>
          <span>Payment:
            {{ ConversionUtility.toStringRupeesConversion(getAllSectionTotals().paymentCash) }}
          </span>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end p-4">
          <CustomButton v-if="isDataExist" title="Print" icon="print" @click="handlePrint" />
        </div>
      </template>
    </Card>
  </main>
</template>

<style>

</style>
